import { Link } from "react-router-dom";
import { Avatar } from "@/components/Avatar";
import { relativeAgo } from "@/lib/presence";
import {
  type FeedItem,
  hrefOf,
  kindLabel,
  subtitleOf,
  summarize,
  toneClass,
  toneOf,
} from "@/lib/auditKinds";

function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** One activity row. Shared by the Work tab, the Security tab and the
 *  per-user drill-down so an event reads identically everywhere. */
export function FeedRow({ item }: { item: FeedItem }) {
  const tone = toneOf(item.kind);
  const href = hrefOf(item);
  const sub = subtitleOf(item);
  const actor = item.actor;

  // An unattributed event either has no human actor at all (a client opening a
  // share link) or only an inferred one (a deal's assigned broker, since stage
  // changes record no mutator). Neither may be phrased as "X did Y".
  const named = actor && item.attributed;

  const body = (
    <>
      {named ? (
        <span className="text-slate-200 font-medium">{actor.name}</span>
      ) : null}
      <span className={named ? "text-slate-400" : "text-slate-300"}>
        {named ? " " : ""}
        {summarize(item)}
      </span>
      {item.title && (
        <span className="text-slate-300"> — {item.title}</span>
      )}
    </>
  );

  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-white/[0.04] last:border-0">
      <span className="text-[11px] tabular-nums text-slate-600 w-[42px] flex-shrink-0 mt-0.5">
        {clock(item.at)}
      </span>

      {actor ? (
        <Avatar name={actor.name || "?"} size="xs" />
      ) : (
        // No avatar slot collapse — keeps every row's text on the same x-axis.
        <span
          className="inline-flex items-center justify-center h-6 w-6 flex-shrink-0 rounded-full bg-violet-500/10 text-violet-300 text-[11px]"
          title="Action taken by a client, not a team member"
        >
          ↗
        </span>
      )}

      <div className="min-w-0 flex-1">
        <div className="text-[13px] leading-snug">
          {href ? (
            <Link to={href} className="hover:underline decoration-white/20">
              {body}
            </Link>
          ) : (
            body
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span
            className={`inline-flex rounded border px-1.5 text-[10px] uppercase tracking-wide ${toneClass(tone)}`}
          >
            {kindLabel(item.kind)}
          </span>
          {/* When the actor is only inferred, name them as the owner rather
              than the subject of the sentence. */}
          {actor && !item.attributed && (
            <span className="text-[11px] text-slate-500">owner {actor.name}</span>
          )}
          {sub && (
            <span className="text-[11px] text-slate-500 truncate">{sub}</span>
          )}
        </div>
      </div>

      <span className="text-[11px] text-slate-600 whitespace-nowrap mt-0.5">
        {relativeAgo(item.at)}
      </span>
    </div>
  );
}
